<template>
  <div class="page-wrap">

    <div v-if="loaded">

      <!-- ── Page Header ── -->
      <div class="page-header">
        <div class="page-header__left">
          <button class="btn-back" @click="$router.back()">
            <i class="fa fa-arrow-left"></i>
          </button>
          <div>
            <h1 class="page-title">{{ order.booked_details.product.title }}</h1>
            <div class="page-meta">
              <span class="meta-item">
                <i class="fa fa-ticket"></i>
                {{ order.booked_details.service_level.title }}
              </span>
              <span class="meta-item">
                <i class="fa fa-map-marker"></i>
                {{ order.booked_details.starting_point.city.name }}
              </span>
              <span class="meta-item">
                <i class="fa fa-cog"></i>
                {{ order.booked_details.service_level
                  ? `${order.booked_details.service_level.pricing_type.replace('_', ' ')} / ${order.booked_details.service_level.availability_type.replaceAll('_', ' ')}`
                  : '—' }}
              </span>
            </div>
          </div>
        </div>
        <div class="page-header__right">
          <span v-html="statusBadge"></span>
        </div>
      </div>

      <!-- ── Info Cards Row ── -->
      <div class="info-grid">
        <div class="info-card">
          <div class="info-card__label">Order ID</div>
          <div class="info-card__value">#{{ order.id }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Cart ID</div>
          <div class="info-card__value">
            <span
                class="link-cell"
                v-if="cartID !== '-'"
                @click="$router.push(`/carts/${cartID}`)"
            >#{{ cartID }}</span>
            <span v-else class="text-muted">—</span>
          </div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Gift ID</div>
          <div class="info-card__value">
            <span
                class="link-cell"
                v-if="giftID !== '-'"
                @click="$router.push(`/gifts/${giftID}`)"
            >#{{ giftID }}</span>
            <span v-else class="text-muted">—</span>
          </div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Status</div>
          <div class="info-card__value" v-html="statusBadge"></div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Contact Person</div>
          <div class="info-card__value">{{ order.contact_person ? order.contact_person.name : '—' }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">User Issuer</div>
          <div class="info-card__value">{{ order.issuer.name }}</div>
        </div>
        <div class="info-card">
          <div class="info-card__label">{{ orderIsPerPerson ? 'Ordered' : 'Items Ordered' }}</div>
          <div class="info-card__value">
            {{ order | calcOrderCount(orderIsPerPerson, dailyNightlyDaysCount || perTimeScopToBookCount) }}
          </div>
        </div>
        <div class="info-card">
          <div class="info-card__label">Passengers</div>
          <div class="info-card__value" v-html="passengersStats"></div>
        </div>
      </div>

      <!-- ── Date Range ── -->
      <div class="panel" v-if="availabilityType !== 'pass'">
        <div class="panel-title"><i class="fa fa-calendar"></i> Schedule</div>
        <div class="date-range">
          <div class="date-box">
            <div class="date-box__label">Starts At</div>
            <div class="date-box__value">{{ order.starts_at || '—' }}</div>
          </div>
          <div class="date-range__arrow">
            <i class="fa fa-long-arrow-right"></i>
          </div>
          <div class="date-box">
            <div class="date-box__label">Ends At</div>
            <div class="date-box__value">{{ order.ends_at || '—' }}</div>
          </div>
        </div>
      </div>

      <!-- ── Leader Info ── -->
      <div class="panel" v-if="leader">
        <div class="panel-title"><i class="fa fa-user"></i> Leader Information</div>
        <div class="leader-grid">
          <div class="leader-field">
            <div class="leader-field__label">Full Name</div>
            <div class="leader-field__value">
              {{ leader.first_name }} {{ leader.last_name }}
              <span class="leader-badge">Leader</span>
            </div>
          </div>
          <div class="leader-field">
            <div class="leader-field__label"><i class="fa fa-phone"></i> Phone</div>
            <div class="leader-field__value">{{ leader.phone_number || '—' }}</div>
          </div>
          <div class="leader-field">
            <div class="leader-field__label"><i class="fa fa-envelope"></i> Email</div>
            <div class="leader-field__value">{{ leader.email || '—' }}</div>
          </div>
        </div>
      </div>

      <!-- ── Pricing ── -->
      <div class="panel">
        <div class="panel-title"><i class="fa fa-credit-card"></i> Pricing</div>
        <div class="price-grid">
          <div class="price-card">
            <div class="price-card__label">Original Price</div>
            <div class="price-card__amount">
              {{ order.total_prices.original.amount }}
              <span class="price-card__currency">{{ order.total_prices.original.currency.code }}</span>
            </div>
          </div>
          <div class="price-card">
            <div class="price-card__label">Agent Price</div>
            <div class="price-card__amount">
              {{ order.total_prices.agent.amount }}
              <span class="price-card__currency">{{ order.total_prices.agent.currency.code }}</span>
            </div>
          </div>
          <div class="price-card price-card--highlight">
            <div class="price-card__label">Sold Price</div>
            <div class="price-card__amount">
              {{ order.total_prices.sold.amount }}
              <span class="price-card__currency">{{ order.total_prices.sold.currency.code }}</span>
            </div>
          </div>
        </div>
      </div>

    </div>

    <!-- Loading -->
    <div class="mt-5 pt-5">
      <loading v-if="!loaded"></loading>
    </div>

  </div>
</template>

<script>
import { config } from "../helpers";
import moment from "moment";

export default {
  name: "Order",
  data() {
    return {
      id: this.$route.params.id,
      loaded: false,
      order: null,
    };
  },

  mounted() {
    this.url = config("BACKEND_ROOT") + "/admin/orders";
    this.loadData();
  },

  methods: {
    loadData() {
      axios.get(`${this.url}/${this.id}`).then((response) => {
        this.order = response.data;
        this.loaded = true;
      });
    },
  },

  computed: {
    statusBadge() {
      const map = {
        Booked:    ['status-badge status-badge--green', 'Booked'],
        Canceled:  ['status-badge status-badge--red',   'Canceled'],
        Cancelled: ['status-badge status-badge--red',   'Canceled'],
        Pending:   ['status-badge status-badge--blue',  'Pending'],
        InCart:    ['status-badge status-badge--gray',  'In Cart'],
      };
      const [cls, label] = map[this.order?.status] ?? ['status-badge status-badge--gray', this.order?.status];
      return `<span class="${cls}">${label}</span>`;
    },

    availabilityType() {
      return this.order.booked_details?.service_level?.availability_type ?? null;
    },

    orderIsPerPerson() {
      if (!this.order.booked_details) return null;
      return this.order.booked_details.service_level.pricing_type === 'per_person';
    },

    dailyNightlyDaysCount() {
      return (
          moment(this.order.ends_at, 'YYYY-MM-DD').diff(
              moment(this.order.starts_at, 'YYYY-MM-DD'),
              'days'
          ) + 1
      );
    },

    perTimeScopToBookCount() {
      return this.order.bookings?.length ?? 0;
    },

    passengersStats() {
      const cats = {};
      for (const p of this.order.passengers) {
        const title = p?.price_category?.title;
        if (!title) return '—';
        cats[title] = (cats[title] || 0) + 1;
      }
      return Object.entries(cats)
          .map(([cat, count]) => `<span class="passenger-badge">${cat} × ${count}</span>`)
          .join('');
    },

    leader() {
      return this.order.passengers.find((p) => p.is_leader);
    },

    giftID() {
      const tag = this.order.tags.find((t) => t.key.startsWith('dd_gift_'));
      return tag ? tag.key.split('_')[2] : '-';
    },

    cartID() {
      const tag = this.order.tags.find((t) => t.key === 'dd_cart_id');
      return tag ? tag.value : '-';
    },
  },

  filters: {
    timeFrameCalc(availability, count) {
      if (availability === 'daily')    return `${count} Day${count > 1 ? 's' : ''}`;
      if (availability === 'nightly')  return `${count} Night${count > 1 ? 's' : ''}`;
      if (availability === 'per_time') return `${count} Scope${count > 1 ? 's' : ''}`;
      return '';
    },
    calcOrderCount(item, isPerPerson, bookingCount) {
      if (isPerPerson) return item.passengers.length;
      return bookingCount ? item.bookings.length / bookingCount : item.bookings.length;
    },
  },
};
</script>

<style scoped>
/* ══════════════════════════════════════
   Page
══════════════════════════════════════ */
.page-wrap {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

/* ── Header ── */
.page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.page-header__left { display: flex; align-items: flex-start; gap: 12px; }

.btn-back {
  width: 34px; height: 34px;
  margin-top: 4px;
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 8px;
  color: var(--text-2);
  cursor: pointer;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  transition: border-color .15s, color .15s;
}
.btn-back:hover { border-color: var(--accent); color: var(--accent); }

.page-title {
  font-size: 20px;
  font-weight: 500;
  color: var(--text-1);
  margin: 0 0 6px;
}
.page-meta { display: flex; flex-wrap: wrap; gap: 16px; }
.meta-item {
  font-size: 12px;
  color: var(--text-3);
  display: flex;
  align-items: center;
  gap: 5px;
}

/* ── Status badges ── */
.status-badge {
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
}
.status-badge--green { background: rgba(34,197,94,.12);   color: #22c55e; }
.status-badge--red   { background: rgba(244,47,75,.12);   color: #f42f4b; }
.status-badge--blue  { background: rgba(129,140,248,.12); color: #818cf8; }
.status-badge--gray  { background: rgba(156,163,175,.12); color: #9ca3af; }

/* ── Info grid ── */
.info-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
}
.info-card {
  background: var(--bg-panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1rem 1.1rem;
}
.info-card__label {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: .5px;
  margin-bottom: 6px;
}
.info-card__value {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-1);
}

.link-cell {
  color: var(--accent);
  cursor: pointer;
  font-weight: 500;
}
.link-cell:hover { text-decoration: underline; }
.text-muted { color: var(--text-3); font-weight: 400; }

.passenger-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 11px;
  background: rgba(129,140,248,.12);
  color: #818cf8;
  margin-right: 4px;
}

/* ── Panel (shared) ── */
.panel {
  background: var(--bg-panel);
  border: 1px solid var(--border);
  border-radius: 12px;
  padding: 1.25rem;
}
.panel-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: .5px;
  margin-bottom: 1.1rem;
  display: flex;
  align-items: center;
  gap: 7px;
}

/* ── Date range ── */
.date-range {
  display: flex;
  align-items: center;
  gap: 20px;
}
.date-box__label {
  font-size: 11px;
  color: var(--text-3);
  margin-bottom: 4px;
}
.date-box__value {
  font-size: 15px;
  font-weight: 500;
  color: var(--text-1);
}
.date-range__arrow {
  color: var(--text-3);
  font-size: 20px;
}

/* ── Leader info ── */
.leader-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
}
.leader-field__label {
  font-size: 11px;
  color: var(--text-3);
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 5px;
}
.leader-field__value {
  font-size: 14px;
  font-weight: 500;
  color: var(--text-1);
  display: flex;
  align-items: center;
  gap: 8px;
}
.leader-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 20px;
  font-size: 10px;
  font-weight: 600;
  background: rgba(244,47,75,.12);
  color: var(--accent);
}

/* ── Pricing ── */
.price-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
}
.price-card {
  background: var(--bg-hover);
  border: 1px solid var(--border);
  border-radius: 10px;
  padding: 1.1rem 1.25rem;
  text-align: center;
}
.price-card--highlight {
  background: rgba(34,197,94,.08);
  border-color: rgba(34,197,94,.25);
}
.price-card__label {
  font-size: 11px;
  font-weight: 500;
  color: var(--text-3);
  text-transform: uppercase;
  letter-spacing: .5px;
  margin-bottom: 8px;
}
.price-card__amount {
  font-size: 26px;
  font-weight: 600;
  color: var(--text-1);
}
.price-card--highlight .price-card__amount { color: #22c55e; }
.price-card__currency {
  font-size: 13px;
  font-weight: 400;
  color: var(--text-3);
  margin-left: 4px;
}

/* ══════════════════════════════════════
   Responsive
══════════════════════════════════════ */
@media (max-width: 1024px) {
  .info-grid   { grid-template-columns: repeat(2, 1fr); }
  .leader-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 600px) {
  .info-grid   { grid-template-columns: 1fr 1fr; }
  .price-grid  { grid-template-columns: 1fr; }
  .leader-grid { grid-template-columns: 1fr; }
  .date-range  { flex-direction: column; align-items: flex-start; gap: 12px; }
  .date-range__arrow { transform: rotate(90deg); }
}
</style>
